import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * How long (ms) the selected variant's image is shown before reverting
 * back to the image that was displayed before the variant was selected.
 */
const VARIANT_PREVIEW_DURATION = 3000;

/**
 * Per-section state so the "base" image (cover, or whatever was showing
 * before a variant was picked) survives the full gallery replacement that
 * happens on every variant update. Keyed by the enclosing section element.
 *
 * @type {WeakMap<Element, { baseMediaId: string | undefined, timer: number | undefined, revertPending: boolean }>}
 */
const variantPreviewState = new WeakMap();

let variantPreviewStylesInjected = false;

function injectVariantPreviewStyles() {
  if (variantPreviewStylesInjected) return;
  variantPreviewStylesInjected = true;

  const style = document.createElement('style');
  style.textContent = `
    media-gallery { position: relative; }
    .variant-preview-countdown {
      position: absolute;
      left: 50%;
      bottom: 14px;
      transform: translateX(-50%);
      width: min(60%, 220px);
      height: 5px;
      border-radius: 999px;
      background: rgba(0, 0, 0, 0.28);
      box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.45), 0 1px 6px rgba(0, 0, 0, 0.25);
      overflow: hidden;
      z-index: 6;
      pointer-events: none;
      opacity: 0;
      animation: variant-preview-fade-in 160ms ease forwards;
    }
    @keyframes variant-preview-fade-in { to { opacity: 1; } }
    .variant-preview-countdown__fill {
      width: 100%;
      height: 100%;
      border-radius: inherit;
      background: var(--variant-preview-color, #e5251f);
      transform: scaleX(1);
      transform-origin: left center;
    }
    @media (prefers-reduced-motion: reduce) {
      .variant-preview-countdown { display: none; }
    }
  `;
  document.head.appendChild(style);
}

/**
 * Returns the product media id rendered inside a slide element.
 * @param {Element | null | undefined} slide
 * @returns {string | undefined}
 */
function slideMediaId(slide) {
  return slide?.querySelector('[data-media-id]')?.getAttribute('data-media-id') ?? undefined;
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Returns the media id of the slide currently shown in this gallery's slideshow.
   * @returns {string | undefined}
   */
  #currentMediaId() {
    const slideshow = this.slideshow;
    const slides = slideshow?.slides;
    if (!slideshow || !slides?.length) return undefined;
    return slideMediaId(slides[slideshow.current]);
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   * After swapping in the selected variant's image, it shows a countdown bar and, once
   * the countdown finishes, reverts the slideshow back to the image that was displayed
   * before the variant was selected (a previously selected image or the cover image).
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const section = this.closest('.shopify-section, dialog');
    const currentMediaId = this.#currentMediaId();

    let state = section ? variantPreviewState.get(section) : undefined;

    // Capture the base image only when a revert isn't already pending, so rapid
    // variant changes keep reverting to the original (cover) image rather than
    // to the previous variant's preview image.
    if (!state || !state.revertPending) {
      state = { baseMediaId: currentMediaId, timer: undefined, revertPending: false };
      if (section) variantPreviewState.set(section, state);
    }

    if (state.timer) {
      clearTimeout(state.timer);
      state.timer = undefined;
    }

    this.replaceWith(newMediaGallery);

    const gallery = /** @type {MediaGallery} */ (newMediaGallery);
    const baseMediaId = state.baseMediaId;

    // The variant's image is rendered first in the new gallery's slideshow.
    const newCurrentMediaId =
      gallery.querySelector('slideshow-component [data-media-id]')?.getAttribute('data-media-id') ?? undefined;

    // Nothing to revert to, no slideshow to drive, or the variant image is
    // already the base image — skip the countdown entirely.
    if (!baseMediaId || !gallery.slideshow || baseMediaId === newCurrentMediaId) {
      state.revertPending = false;
      return;
    }

    state.revertPending = true;
    MediaGallery.startVariantPreviewCountdown(gallery, state);
  };

  /**
   * Shows the countdown bar on the freshly-swapped gallery and schedules the revert.
   * @param {MediaGallery} gallery - The newly connected gallery showing the variant image.
   * @param {{ baseMediaId: string | undefined, timer: number | undefined, revertPending: boolean }} state
   */
  static startVariantPreviewCountdown(gallery, state) {
    injectVariantPreviewStyles();

    const bar = document.createElement('div');
    bar.className = 'variant-preview-countdown';
    const fill = document.createElement('div');
    fill.className = 'variant-preview-countdown__fill';
    bar.appendChild(fill);
    gallery.appendChild(bar);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fill.style.transition = `transform ${VARIANT_PREVIEW_DURATION}ms linear`;
        fill.style.transform = 'scaleX(0)';
      });
    });

    state.timer = window.setTimeout(() => {
      state.timer = undefined;
      state.revertPending = false;
      bar.remove();

      const slideshow = gallery.slideshow;
      const slides = slideshow?.slides;
      if (!slideshow || !slides?.length || !state.baseMediaId) return;

      const targetIndex = slides.findIndex((slide) => slideMediaId(slide) === state.baseMediaId);
      if (targetIndex >= 0) {
        slideshow.select(targetIndex, undefined, { animate: true });
      }
    }, VARIANT_PREVIEW_DURATION);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
